import { Platform, PlatformOSType } from 'react-native';

import AsyncStorage from '@react-native-async-storage/async-storage';

import { URLS, isDocumentFilled, isDocumentLinkFilled } from '@/data';
import { STATES } from '@/feature/kyc/const';
import {
    GlobalConfigurationsDocument,
    GlobalConfigurationsDocumentDataBodyBooleanFlagsSlice,
    GlobalConfigurationsDocumentDataBodyButtonSlice,
    GlobalConfigurationsDocumentDataBodyKeyValuePairsSlice,
    GlobalConfigurationsDocumentDataBodyLaunchpromoSlice,
    GlobalConfigurationsDocumentDataBodyRichTextSlice,
    GlobalConfigurationsDocumentDataBodyScreenLinksSlice,
    GlobalConfigurationsDocumentDataBodyScreenLinksSliceItem,
    GlobalConfigurationsDocumentDataBodyStatesSelectorSlice,
    GlobalConfigurationsDocumentDataBodyStatesSelectorSliceItem,
    GlobalConfigurationsDocumentDataBodyStatesSelectorSlicePrimary,
    ProductDocument,
    ProductDocumentDataBodyAppMaintenanceSettingsSlice,
    ProductDocumentDataBodyFlagsSlice,
    ProductDocumentDataBodyFtdSettingsSlice,
    ProductDocumentDataBodyFtdSettingsSlicePrimary,
    ProductDocumentDataBodyKeyValuePairsSlice,
    ProductDocumentDataBodyLeaguepagetabsorderSlice,
    ProductDocumentDataBodyListSlice,
    ProductDocumentDataBodyLobbycomponentsSlice,
    ProductDocumentDataBodyRichTextSlice,
    ProductDocumentDataBodyScreenLinksSlice,
    ProductDocumentDataBodyScreenLinksSliceItem,
    ProductDocumentDataBodyTabsettingsSlice,
} from '@/types/prismic.generated';
import { logger } from '@/utils/logging';
import {
    AppFeatureFlags,
    BooleanFlagKeys,
    findAllSlices,
    findSlice,
    parseBooleanFlags,
    parseButtons,
    parseRichTextSlices,
} from '@/utils/parse-prismic-content';
import { RichTextField } from '@prismicio/client';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export enum Product {
    Pickem = 'pickem',
    Sweeps = 'sweeps',
    Arcade = 'arcade',
    Casino = 'casino',
    Prediction = 'prediction',
    Rips = 'rips',
    None = 'none', //indicates a state that is in-eligible for all products
}

export const Jurisdiction = {
    FANTASY: 'FANTASY',
    ARCADE: 'ARCADE',
    SWEEPSCASH: 'CASH-SBK',
    SWEEPSCOIN: 'COIN-SBK',
    CASINOCASH: 'CASH-CASINO',
    CASINOCOIN: 'COIN-CASINO',
    PREDICTION: 'PREDICTION',
    RIPS: 'TRADING-CARDS',
} as const;

export enum ProductConfigKeys {
    QuickAmounts = 'Quick Amounts',
    ContentKeys = 'Content Keys',
    ApiKeys = 'API keys',
    Urls = 'URLS',
    LobbyOrdering = 'LobbyOrdering',
    LobbyFeatureLeague = 'Feature League',
    FeaturedLineups = 'Featured Lineups',
    PickslipConfig = 'PickslipConfig',
    PickBarAmount = 'Pick Bar Amount',
    RaceToPlaceAmounts = 'Race To Place Amounts',
    RaceToPlaceInfo = 'Race to Place Info',
    PredictionsConfig = 'Predictions Config',
}

export enum GlobalConfigKeys {
    Urls = 'Urls',
    ApiKeys = 'ApiKeys',
    SelfExclusionAdd = 'SelfExclusionAdd',
    SelfExclusionAddDefault = 'SelfExclusionAddDefault',
    UserSettings = 'UserSettings',
    AppRateDialogSettings = 'AppRateDialogSettings',
    QuickDepositButtonsFantasy = 'QuickDepositButtonsFantasy',
    BeforeYouDepositInfo = 'BeforeYouDepositInfo',
    DefaultProductOnboardingDescription = 'DefaultProductOnboardingDescription',
    TransactionSettings = 'TransactionSettings',
    AppUpdate = 'AppUpdate',
    DatadogSettings = 'DatadogSettings',
    TrustlySettings = 'TrustlySettings',
    TrustedWebviewHosts = 'TrustedWebviewHosts',
}

export type JurisdictionSource = 'detected' | 'manual';

/**
 * All the specific jurisdiction settings should be part of this type
 */
export type JurisdictionSettings = {
    globalSettings?: GlobalSettings;
    productConfig?: ProductConfig | undefined;
    productType: Product;
    products?: ProductConfig[] | undefined;
    ineligibleStates?: string[];
};

export type ProductSettings = {
    withdraw_enabled?: boolean;
    deposit_enabled?: boolean;
    deposit_instant_bank_transfers_enabled?: boolean;
    deposit_debit_card_enabled?: boolean;
    deposit_pay_by_bank_enabled?: boolean;
    deposit_venmo_enabled?: boolean;
    deposit_paypal_enabled?: boolean;
    reality_check_enabled?: boolean;
    sgp_enabled?: boolean;
    higher_multiplier_toggle_enabled?: boolean;
    enable_inplay_current_value?: boolean;
    hide_app_bar_raf?: boolean;
    sbk_lobby_featured_events_enabled?: boolean;
    default_opened_scoreboard?: boolean;
    sbk_lobby_featured_bets_enabled?: boolean;
    game_tracker_enabled?: boolean;
    scoreboard_enabled?: boolean;
    live_hub_enabled?: boolean;
    guest_session_enabled?: boolean;
    enable_gradient_background_game_tray?: boolean;
    enable_gradient_background_game_tray_ios?: boolean;
    enable_gradient_background_game_tray_android?: boolean;
    enable_quick_amounts_sheet?: boolean;
    enable_trustly_kyc?: boolean;
    enable_live_market_websockets?: boolean;
    enable_live_game_periods?: boolean;
    enable_trustly_deposit_method?: boolean;
    enable_share_and_reuse_lineups?: boolean;
    enable_3ds2_on_adding_cards?: boolean;
    enable_trustly_for_manually_kyc_users?: boolean;
    enable_trustly_available_funds_guidance?: boolean;
    enable_fantasy_money_purchase?: boolean;
    enable_fantasy_money_redemption?: boolean;
    enable_round_details?: boolean;
    enable_fantasy_fund_with_ssbk?: boolean;
    social_feed_enabled?: boolean;
    ai_match_preview_enabled?: boolean;
    enable_new_platform?: boolean;
    league_navigation_enabled?: boolean;
};

export type ProductConfig = {
    uid: string;
    promohubUID: string;
    settings: ProductSettings;
    keyValuePairs: Record<ProductConfigKeys, Record<string, string>>;
    richTextsSlices: Record<string, RichTextField>;
    screenLinks: ProductDocumentDataBodyScreenLinksSliceItem[];
    gameplayOfflineStates: string[];
    disabledPlatforms: PlatformOSType[];
    disabledUsers: string[];
    lobbyComponents: ProductDocumentDataBodyLobbycomponentsSlice | undefined;
    sectionsOrdering: ProductDocumentDataBodyLeaguepagetabsorderSlice | undefined;
    appMaintenanceConfig?: ProductDocumentDataBodyAppMaintenanceSettingsSlice;
    rafV2PromotionUid: string;
    pickTypesEducationalUid?: string;
    whatsNewUid?: string;
    tabSettings?: ProductDocumentDataBodyTabsettingsSlice;
    ftdSetting?: ProductDocumentDataBodyFtdSettingsSlicePrimary;
    faqQuestionsUid: string;
    reviveBannerUid?: string;
    rescueBannerUid?: string;
    arcadeConfigUid?: string;
    launchSlidesUid?: string;
    nonNavigableCategories?: string[];
};

type GlobalSettings = {
    screen_links: GlobalConfigurationsDocumentDataBodyScreenLinksSliceItem[];
    keyValuePairs: Record<GlobalConfigKeys, Record<string, string>>;
    richTextsSlices: Record<string, RichTextField>;
    buttons: Record<string, { label: string; url: any }>;
    featureFlags: AppFeatureFlags;
    depositSalvageOptions: AppFeatureFlags;
    statesSelector: State[];
    stateAvailabilityImage: GlobalConfigurationsDocumentDataBodyStatesSelectorSlicePrimary | undefined;
    /**
     * Legacy launch slides from the ACS global config `launchpromo` slice. Used as a fallback by
     * `useLaunchSlides` when the current product has no `launch_slides` custom-type document.
     */
    launchPromo: GlobalConfigurationsDocumentDataBodyLaunchpromoSlice[] | undefined;
    gameTrackerConfigUid?: string;
    promoCodeDefaultProductSyncUid?: string;
};

type GlobalConfig = {
    title: string;
    globalSettings: GlobalSettings;
    defaultProduct?: ProductConfig;
    products: ProductConfig[];
    productType: Product;
    ineligibleStates: string[];
};

export type PrismicState = {
    state_name: string;
};

export type UserGPSData = {
    latitude: number;
    longitude: number;
    timestampMillis: number;
    state: string;
    country: string;
};

/**
 * Store that holds the user US-state (determined at app startup), and the betting mode selected via the product switcher.
 */
interface JurisdictionStore {
    userGpsData: UserGPSData | undefined;
    /**
     * The currently active product in the app
     */
    product: Product;
    products: ProductConfig[];
    jurisdiction: string | undefined;
    jurisdictionSource: JurisdictionSource | undefined;
    jurisdictionSettings: JurisdictionSettings | undefined;
    _hydrated: boolean;
    actions: {
        setHydrated: () => void;
        setGpsData: (data: UserGPSData) => void;
        clearGpsData: () => void;
        setProduct: (product: Product) => void;
        setJurisdictionAndUpdateSettings: (state: string, source: JurisdictionSource) => Promise<JurisdictionSettings>;
        fetchSettings(state: string): Promise<JurisdictionSettings>;
        refetchAndUpdateSettings(): Promise<void>;
    };
}

const parseKeyValuePairs = (slices: ProductDocumentDataBodyKeyValuePairsSlice[] | undefined) => {
    const data: Record<string, Record<string, string>> = {};
    slices?.forEach(slice => {
        const values = slice.items?.reduce((acc: { [x: string]: string }, item) => {
            if (item.key) {
                acc[item.key] = item.value ?? '';
            }
            return acc;
        }, {});
        if (slice.primary.name) {
            data[slice.primary.name] = values;
        }
    });
    return data;
};

export const getProductType = (uid: string): Product => {
    switch (true) {
        case uid.includes('pickem'):
            return Product.Pickem;
        case uid.includes('sweeps'):
            return Product.Sweeps;
        case uid.includes('arcade'):
            return Product.Arcade;
        case uid.includes('casino'):
            return Product.Casino;
        case uid.includes('prediction'):
            return Product.Prediction;
        case uid.includes('rips'):
            return Product.Rips;
        default:
            return Product.None;
    }
};

export type State = {
    label: string;
    value: string;
};

// filters out any null state names and returns the corresponding state object
const getStatesWithNames = (
    states: GlobalConfigurationsDocumentDataBodyStatesSelectorSliceItem[] | undefined
): State[] => {
    if (!states) {
        return [];
    }
    return states
        .filter(st => st.state_name !== null)
        .map(
            ({ state_name }) =>
                STATES.find(state => state.value.toLowerCase() === (state_name ?? '').toLowerCase()) || {
                    label: '',
                    value: '',
                }
        );
};

export enum AccountLinksKeys {
    AccountLinks = 'Account Links',
    AccountLinksWeb = 'Account Links Web',
}

const getScreenLinksByKey = (
    slices: ProductDocumentDataBodyScreenLinksSlice[],
    key: AccountLinksKeys
): ProductDocumentDataBodyScreenLinksSlice | undefined => {
    return slices.find(slice => slice?.primary?.screen_name === key);
};

const VALID_PLATFORMS = ['ios', 'android', 'web'] as const;

const isPlatformOSType = (platform: string | undefined): platform is PlatformOSType => {
    return typeof platform === 'string' && (VALID_PLATFORMS as readonly string[]).includes(platform);
};

const parseProductConfig = (product: ProductDocument): ProductConfig => {
    const screenLinksSlices = findAllSlices<ProductDocumentDataBodyScreenLinksSlice>('screen_links', product.data.body);

    // retrieve both account links configs
    const webScreenLinksSlice = getScreenLinksByKey(screenLinksSlices, AccountLinksKeys.AccountLinksWeb);
    const mobileScreenLinksSlice = getScreenLinksByKey(screenLinksSlices, AccountLinksKeys.AccountLinks);

    const screenLinksSlice = Platform.select({
        default: mobileScreenLinksSlice,
        // if no links are present for web, default to mobile feature flags
        web: (webScreenLinksSlice?.items || [])?.length > 0 ? webScreenLinksSlice : mobileScreenLinksSlice,
    });

    const flagsSlices = findSlice<ProductDocumentDataBodyFlagsSlice>('flags', product.data.body);

    const settings: ProductSettings =
        flagsSlices?.items?.reduce((acc: { [x: string]: boolean }, flag) => {
            if (flag.key) {
                acc[flag.key] = flag.enabled;
            }
            return acc;
        }, {}) ?? {};

    const keysSlices = findAllSlices<ProductDocumentDataBodyKeyValuePairsSlice>('key_value_pairs', product.data.body);

    const keyValuePairs = parseKeyValuePairs(keysSlices);

    const richTextSlices = findAllSlices<ProductDocumentDataBodyRichTextSlice>('rich_text', product.data.body);

    const richTextsSlices = parseRichTextSlices(richTextSlices);

    const lobbyComponents = findSlice<ProductDocumentDataBodyLobbycomponentsSlice>(
        'lobbycomponents',
        product.data.body
    );

    const sectionsOrdering = findSlice<ProductDocumentDataBodyLeaguepagetabsorderSlice>(
        'leaguepagetabsorder',
        product.data.body
    );

    const appMaintenanceConfig = findSlice<ProductDocumentDataBodyAppMaintenanceSettingsSlice>(
        'app_maintenance_settings',
        product.data.body
    );

    const tabSettings = findSlice<ProductDocumentDataBodyTabsettingsSlice>('tabsettings', product.data.body);

    const ftdSetting = findSlice<ProductDocumentDataBodyFtdSettingsSlice>('ftd_settings', product.data.body)?.primary;

    const nonNavigableCategoriesSlice = findAllSlices<ProductDocumentDataBodyListSlice>('list', product.data.body).find(
        slice => slice.primary.item_title === 'non_navigable_categories'
    );
    const nonNavigableCategories = (nonNavigableCategoriesSlice?.items ?? [])
        .map(it => it.content ?? '')
        .filter(Boolean);

    const raf = product.data.raf;
    const isRafDocumentFilled = isDocumentFilled(raf);
    const rafV2PromotionUid = isRafDocumentFilled ? raf.uid : '';
    const faqQuestions = product.data.faq_questions;
    const isFaqDocumentFilled = isDocumentFilled(faqQuestions);
    const faqQuestionsUid = isFaqDocumentFilled ? faqQuestions.uid : '';
    const arcadeConfig = product.data.skill_games_config;
    const isArcadeConfigDocumentFilled = isDocumentFilled(arcadeConfig);
    const arcadeConfigUid = isArcadeConfigDocumentFilled ? arcadeConfig.uid : '';

    const launchSlidesConfig = product.data.launch_slides;
    const isLaunchSlidesDocumentFilled = isDocumentFilled(launchSlidesConfig);
    const launchSlidesUid = isLaunchSlidesDocumentFilled ? launchSlidesConfig.uid : '';

    const reviveBannerInfo = product.data.revive_banner;
    const isReviveBannerDocumentFilled = isDocumentLinkFilled(reviveBannerInfo);
    const reviveBannerUid = isReviveBannerDocumentFilled ? reviveBannerInfo.uid : '';

    const rescueBannerInfo = product.data.rescue_banner;
    const isRescueBannerDocumentFilled = isDocumentLinkFilled(rescueBannerInfo);
    const rescueBannerUid = isRescueBannerDocumentFilled ? rescueBannerInfo.uid : '';

    const promohub = product.data.promohub;
    const isPromohubDocumentFilled = isDocumentFilled(promohub);
    const promohubUID = isPromohubDocumentFilled ? promohub.uid : '';
    const whatsNew = product.data.whats_new;
    const isWhatsNewDocumentFilled = isDocumentFilled(whatsNew);
    const whatsNewUid = isWhatsNewDocumentFilled ? whatsNew.uid : undefined;

    const pickTypesEducational = product.data.pick_types_educational;
    const isPickTypesEducationalDocumentFilled = isDocumentFilled(pickTypesEducational);
    const pickTypesEducationalUid = isPickTypesEducationalDocumentFilled ? pickTypesEducational.uid : undefined;

    return {
        uid: product.uid,
        screenLinks: screenLinksSlice?.items ?? [],
        keyValuePairs,
        richTextsSlices,
        settings,
        gameplayOfflineStates: (product.data.gameplay_offline_states ?? []).map(it => it.state_name ?? ''),
        disabledPlatforms: (product.data.disabled_platforms ?? [])
            .map(it => it.platform?.toLowerCase())
            .filter(isPlatformOSType),
        disabledUsers: (product.data.disabled_users ?? []).map(it => it.user_email ?? ''),
        nonNavigableCategories,
        promohubUID: promohubUID ?? '',
        pickTypesEducationalUid: pickTypesEducationalUid,
        whatsNewUid: whatsNewUid,
        lobbyComponents,
        appMaintenanceConfig,
        rafV2PromotionUid: rafV2PromotionUid ?? '',
        faqQuestionsUid: faqQuestionsUid ?? '',
        reviveBannerUid: reviveBannerUid ?? '',
        rescueBannerUid: rescueBannerUid ?? '',
        arcadeConfigUid,
        launchSlidesUid,
        tabSettings,
        ftdSetting,
        sectionsOrdering,
    };
};

const parseGlobalSettings = (config: GlobalConfigurationsDocument): GlobalSettings => {
    const data = config.data.body;
    const screenLinksSlice = findSlice<GlobalConfigurationsDocumentDataBodyScreenLinksSlice>('screen_links', data);
    const keysSlices = findAllSlices<GlobalConfigurationsDocumentDataBodyKeyValuePairsSlice>('key_value_pairs', data);
    const richTextSlices = findAllSlices<GlobalConfigurationsDocumentDataBodyRichTextSlice>('rich_text', data);
    const booleanFlagSlices = findAllSlices<GlobalConfigurationsDocumentDataBodyBooleanFlagsSlice>(
        'boolean_flags',
        data
    );
    const statesSelector =
        findSlice<GlobalConfigurationsDocumentDataBodyStatesSelectorSlice>('states_selector', data)?.items || [];
    const stateAvailabilityImage = findSlice<GlobalConfigurationsDocumentDataBodyStatesSelectorSlice>(
        'states_selector',
        data
    )?.primary;
    const keyValuePairs = parseKeyValuePairs(keysSlices);
    const richTextsSlices = parseRichTextSlices(richTextSlices);
    const buttonSlices = findAllSlices<GlobalConfigurationsDocumentDataBodyButtonSlice>('button', data);
    const buttons = parseButtons(buttonSlices);
    const launchPromo = findAllSlices<GlobalConfigurationsDocumentDataBodyLaunchpromoSlice>('launchpromo', data) || [];

    // retrieve both configs for feature flags
    const mobileFeatureFlags = parseBooleanFlags(booleanFlagSlices, BooleanFlagKeys.AppFeatureFlags);
    const webFeatureFlags = parseBooleanFlags(booleanFlagSlices, BooleanFlagKeys.AppFeatureFlagsWeb);

    const featureFlags = Platform.select({
        default: mobileFeatureFlags,
        // if no feature flags are present for web, default to mobile feature flags
        web: Object.keys(webFeatureFlags).length > 0 ? webFeatureFlags : mobileFeatureFlags,
    });

    // retrieve both configs for deposit salvage options
    const mobileDepositSalvageOptions = parseBooleanFlags(
        booleanFlagSlices,
        BooleanFlagKeys.DepositSalvageOptionSettings
    );
    const webDepositSalvageOptions = parseBooleanFlags(
        booleanFlagSlices,
        BooleanFlagKeys.DepositSalvageOptionSettingsWeb
    );

    const depositSalvageOptions = Platform.select({
        default: mobileDepositSalvageOptions,
        // if no deposit salvage options are present for web, default to mobile deposit salvage options
        web: Object.keys(webDepositSalvageOptions).length > 0 ? webDepositSalvageOptions : mobileDepositSalvageOptions,
    });

    const gameTrackerConfig = config.data.game_tracker_config;
    const isGameTrackerConfigDocumentFilled = isDocumentFilled(gameTrackerConfig);
    const gameTrackerConfigUid = isGameTrackerConfigDocumentFilled ? gameTrackerConfig.uid : '';

    const promoCodeDefaultProductSync = config.data.promo_code_default_product_sync;
    const isPromoCodeDefaultProductSyncDocumentFilled = isDocumentFilled(promoCodeDefaultProductSync);
    const promoCodeDefaultProductSyncUid = isPromoCodeDefaultProductSyncDocumentFilled
        ? promoCodeDefaultProductSync.uid
        : '';

    return {
        screen_links: screenLinksSlice?.items ?? [],
        keyValuePairs,
        richTextsSlices,
        buttons,
        featureFlags,
        depositSalvageOptions,
        launchPromo,
        statesSelector: getStatesWithNames(statesSelector),
        stateAvailabilityImage: stateAvailabilityImage,
        gameTrackerConfigUid,
        promoCodeDefaultProductSyncUid,
    };
};

export class ConfigFetchError extends Error {}

const fetchACSConfigs = async (state: string): Promise<GlobalConfig | undefined> => {
    try {
        const configState = state.toLowerCase();
        const response = await fetch(`${URLS.ACS_URL}/${configState}`, {
            method: 'GET',
        });
        const data = await response.json();
        const globalSettings = parseGlobalSettings(data);
        const ineligibleStates: PrismicState[] = data.data.ineligible_states;
        const inIneligibleState = !!ineligibleStates?.find(it => it.state_name === state);
        const products: ProductConfig[] = data.data?.products.map(parseProductConfig) ?? [];

        let defaultProduct: ProductConfig | undefined = parseProductConfig(data.data?.default_product);

        if (defaultProduct.disabledPlatforms.includes(Platform.OS)) {
            //If the default product is not available for the current platform, we will use the first available product
            //that is available for the current platform
            defaultProduct = undefined;
            defaultProduct = products.find(it => !it.disabledPlatforms.includes(Platform.OS));
        }

        //If we did not find a default product, we will use None, to indicate we are in an ineligible location or platform
        const productType: Product =
            inIneligibleState || defaultProduct === undefined ? Product.None : getProductType(defaultProduct.uid);

        return {
            title: data.data?.title,
            globalSettings,
            defaultProduct,
            products,
            ineligibleStates: ineligibleStates?.map(st => st.state_name) || [],
            productType,
        };
    } catch (e) {
        console.warn('Error fetching ACS config', e);
        throw new ConfigFetchError('Error fetching ACS config.');
    }
};

const TAG = '[JurisdictionStore]';

export const useJurisdictionStore = create<JurisdictionStore>()(
    persist(
        (set, get) => {
            return {
                userGpsData: undefined,
                product: Product.None,
                jurisdiction: undefined,
                jurisdictionSource: undefined,
                jurisdictionSettings: undefined,
                productConfig: undefined,
                products: [],
                _hydrated: false,
                actions: {
                    setGpsData: (data: UserGPSData) => {
                        set({ userGpsData: data });
                    },
                    clearGpsData: () => {
                        set({ userGpsData: undefined });
                    },
                    setProduct: product => {
                        logger.info(TAG, 'setProduct', product);
                        const productConfig = get().jurisdictionSettings?.products?.find(it =>
                            it.uid.includes(product)
                        );
                        const settings = get().jurisdictionSettings;
                        set({ product, jurisdictionSettings: { ...settings, productType: product, productConfig } });
                    },
                    setJurisdictionAndUpdateSettings: async (state, source) => {
                        logger.info(TAG, 'set jurisdiction and update settings', state, source);
                        const settings = await get().actions.fetchSettings(state);
                        set({
                            jurisdiction: state,
                            jurisdictionSource: source,
                            jurisdictionSettings: settings,
                            products: settings?.products,
                            product: settings?.productType,
                        });

                        return settings;
                    },
                    fetchSettings: async (state: string) => {
                        logger.info(TAG, 'fetching jurisdiction settings', state);
                        const settings = await fetchACSConfigs(state);

                        return {
                            products: settings?.products ?? [],
                            globalSettings: settings?.globalSettings,
                            productConfig: settings?.defaultProduct,
                            productType: settings?.productType ?? Product.None,
                            ineligibleStates: settings?.ineligibleStates,
                        };
                    },
                    setHydrated: () => {
                        set({ _hydrated: true });
                    },
                    refetchAndUpdateSettings: async () => {
                        const { jurisdiction } = get();
                        if (!jurisdiction) {
                            return;
                        }
                        const updatedSettings = await get().actions.fetchSettings(jurisdiction);

                        const currentProduct = get().product;
                        const currentProductUpdatedConfig =
                            updatedSettings.products?.find(it => it.uid.includes(currentProduct)) ??
                            updatedSettings.productConfig;

                        set({
                            jurisdictionSettings: {
                                ...updatedSettings,
                                productConfig: currentProductUpdatedConfig,
                                // ensure we are not overriding the current product
                                productType: currentProduct,
                            },
                        });
                    },
                },
            };
        },
        {
            name: 'jurisdiction-storage',
            version: 8,
            storage: createJSONStorage(() => AsyncStorage),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            partialize: ({ actions, _hydrated, ...rest }: JurisdictionStore) => rest,
            onRehydrateStorage: () => store => {
                store?.actions.setHydrated();
            },
        }
    )
);
